import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import FormField from '../../FormField/FormField';
import { useDebounce } from '../../../hooks/useDebounce';
import { useValidateTaxNumberMutation } from '../../../store/services/addSellerService';

interface Props {
  dataTestId?: string;
  skipInitialValidation?: boolean;
}

export default function DebouncedTaxNumber({
  dataTestId,
  skipInitialValidation = false,
}: Props) {
  const { t } = useTranslation();
  const { watch, setError, clearErrors } = useFormContext();

  const value = watch('taxNumber');
  const debouncedValue = useDebounce(value, 500);
  const lastCheckedRef = useRef<string | null>(null);

  const [validateTaxNumber, { isLoading }] = useValidateTaxNumberMutation();

  const requiredError = t('components.validations.requiredField');

  const taxSchema = z
    .string({ message: requiredError })
    .min(1, { message: requiredError })
    .regex(/^[0-9]+$/, { message: t('components.validations.acceptNumbersOnly') })
    .min(4, { message: t('components.validations.taxLength') })
    .max(15, { message: t('components.validations.taxLength') });

  useEffect(() => {
    if (skipInitialValidation) return;
    if (!debouncedValue) return;
    if (lastCheckedRef.current === debouncedValue) return;
    lastCheckedRef.current = debouncedValue;

    const { success } = taxSchema.safeParse(debouncedValue);
    if (!success) return;

    let cancelled = false;

    (async () => {
      try {
        await validateTaxNumber({ taxNumber: debouncedValue }).unwrap();

        if (cancelled) return;
        clearErrors('taxNumber');
      } catch (err: unknown) {
        if (cancelled) return;
        const error = err as { data?: { message?: string } };
        setError('taxNumber', {
          type: 'validate',
          message: error?.data?.message || requiredError,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, skipInitialValidation]);

  return (
    <FormField
      id="taxNumber"
      name="taxNumber"
      type="text"
      label={t('sellers.addSeller.taxNumber')}
      placeholder={t('sellers.addSeller.taxNumberPlaceholder')}
      disabled={isLoading}
      data-testid={dataTestId || 'add-seller-tax-number'}
    />
  );
}
